import React, { useState, useEffect } from "react";
import axios from "axios";
import { serverUrl } from "../../authConfig";
import { useParams, Link } from "react-router-dom";
import moment from "moment";
import LoadingComponent from "./LoadingComponent";
import { FaUserTie, FaMapMarkerAlt, FaClock, FaEnvelope } from "react-icons/fa";

const PublicEventViewer = () => {
  const [formData, setFormData] = useState(null);
  const [eventNotFound, setEventNotFound] = useState(false);
  const [loading, setLoading] = useState(false);
  const { event_id } = useParams();

  useEffect(() => {
    const fetchEvent = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${serverUrl}/events/publicEventDetails/${event_id}`);
        const processed = { ...res.data.formData };

        // Optional: clean or rebuild any required structure here
        if (processed.students?.groupStudentsMap) {
          const rebuilt = {};
          for (const [groupId, studentArray] of Object.entries(processed.students.groupStudentsMap)) {
            rebuilt[groupId] = new Set(studentArray);
          }
          processed.students.groupStudentsMap = rebuilt;
        }

        setFormData(processed);
      } catch (error) {
        console.error("Error loading event:", error);
        setEventNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [event_id]);

  if (loading) return <LoadingComponent message="Loading event details..." />;

  if (eventNotFound || !formData) {
    return (
      <div className="p-6 max-w-3xl mx-auto text-center text-red-600 font-semibold text-lg">
        Sorry, this event could not be found.
      </div>
    );
  }

  const { eventName, eventDescription, preferredDate, startTime, endDate, endTime, locations, staff } = formData;
  const organiser = staff?.organiser;

  return (
    <div className="p-6 max-w-3xl mx-auto bg-white rounded-xl shadow-md space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-blue-800 mb-2">{eventName}</h1>
        <p className="text-lg italic text-gray-700">{eventDescription}</p>
      </div>

      <div className="space-y-2">
        {organiser && (
          <div className="flex items-center gap-2 text-gray-800">
            <FaUserTie />
            <span>{organiser.label}</span>
            {organiser.email && (
              <>
                <FaEnvelope className="ml-4" />
                <span>{organiser.email}</span>
              </>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 text-gray-800">
          <FaClock />
          <span>
            Start:{" "}
            {preferredDate && moment(preferredDate).format("dddd, D MMM YYYY")}
            {startTime && ` @ ${moment(startTime, "HH:mm").format("h:mm A")}`}
          </span>
        </div>

        <div className="flex items-center gap-2 text-gray-800">
          <FaClock />
          <span>
            Finish:{" "}
            {endDate && moment(endDate).format("dddd, D MMM YYYY")}
            {endTime && ` @ ${moment(endTime, "HH:mm").format("h:mm A")}`}
          </span>
        </div>

        {locations?.length > 0 && (
          <div className="flex items-start gap-2 text-gray-800">
            <FaMapMarkerAlt className="mt-1" />
            <div>
              <div className="font-semibold">Locations:</div>
              <ul className="list-disc list-inside text-sm text-gray-700">
                {locations.map((loc, index) => (
                  <li key={index}>{loc.address}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div>
          <div className="font-semibold text-gray-800">Staff Attending:</div>
          {staff?.attending?.length > 0 ? (
            <ul className="list-disc list-inside text-sm text-gray-700 mt-1">
              {staff.attending.map((s, index) => (
                <li key={index}>{s.label}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-600 italic">No staff listed</p>
          )}
        </div>
      </div>

      <div className="border-t pt-4 text-sm text-gray-600 text-center">
        Redeemer Lutheran College staff can view full event details by{" "}
        <Link
          to={`/viewEvent/${event_id}`}
          className="text-blue-700 font-medium hover:underline"
        >
          signing in
        </Link>.
      </div>
    </div>
  );
};

export default PublicEventViewer;
